import axios, { AxiosInstance } from 'axios'
import { EMethod, IFactoryFunctionRequest, IFactoryResponse, IOffice, IPaginated} from "@bc-library/interface";
import { BASE_URL, API_MAIN_VERSION } from '@/config/constantes';
import useAxios from "@/services/api/useAxios";
import { beforeSending } from '../invoicingProjects/hooks'

const resource = 'invoicingProjects'

type optionsInvoice = {
  _firmId: string;
  _id: string;
};

const postNewLine = (axios: IFactoryFunctionRequest<optionsInvoice, undefined>) => ({projectId, data}: {projectId: string, data: any}) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/project/${projectId}`,
    data,
  })
};

const getAllInvoices = (axios: IFactoryFunctionRequest<IPaginated<TShowInvoice>, undefined>) => (query: { limit: number, page: number, status: string, search: string, }) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/all`,
    query,
  })
};

const updateInvoice = (axios: IFactoryFunctionRequest<any, undefined>) => ({ _id, ...data }: { _id: string }) => {
  return axios({
      method: EMethod.put,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${_id}`,
      data,
      beforeSendingHook: beforeSending
  })
}

const updateInvoiceStatus = (axios: IFactoryFunctionRequest<any, undefined>) => ({ _id, newStatus }: { _id: string, newStatus: number }) => {
  return axios({
      method: EMethod.put,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/updateStatus/${_id}`,
      data: {newStatus},
  })
}

const addLog = (axios: IFactoryFunctionRequest<any, undefined>) => (invoiceId: string, actionId: number, options ) => {
  return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/newLog/${invoiceId}/action/${actionId}`,
      data: options,
  })
}

const deleteInvoice = (axios: IFactoryFunctionRequest<any, undefined>) => ({ _id }: { _id: string }) => {
  return axios({
      method: EMethod.delete,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${_id}`,
  })
}


const getInvoiceNumber = (axios: IFactoryFunctionRequest<any, any>) => (invoicingInformation: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/invoiceNumber/${invoicingInformation}`,
  })
}

const getInvoiceDraft = (axios: IFactoryFunctionRequest<any, any>) => (invoiceId: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/draft/${invoiceId}`,
  })
}

const emitInvoice = (axios: IFactoryFunctionRequest<any, any>) => (invoiceId: string, invoiceNumber: any) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/emitInvoice/${invoiceId}`,
    data: {invoiceNumber}
  })
}

const newEmittedCreditNote = (axios: IFactoryFunctionRequest<any, any>) => ({projectId, data}: {projectId: string, data: any}) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/newEmittedCreditNote/${projectId}`,
    data: data
  })
}

const addCollectionToInvoices = (axios: IFactoryFunctionRequest<any, any>) => (data) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/addCollectionToInvoices`,
    data: data,
    beforeSendingHook: beforeSending
  })
}

const reconcileBankStatement = (axios: IFactoryFunctionRequest<any, any>) => (data: { file: string; filename: string }) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/reconcile`,
    data: data,
    immediate: false,
  })
}


const deleteLog = (axios: IFactoryFunctionRequest<any, any>) => (invoiceId: string, logId: string) => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/removeLog/${invoiceId}/log/${logId}`,
  })
}

const globalSearch = (axios: IFactoryFunctionRequest<any, any>) => (searchString = '') => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/search`,
    query: { searchString },
    immediate: false,
  })
}

const exportInvoicesToExcel = (axios: IFactoryFunctionRequest<{ base64: string }, any>) => (query: Record<string, any>) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/export`,
    query,
    immediate: false,
  })
}

const submitEInvoice = (axios: IFactoryFunctionRequest<any, any>) => (invoiceId: string) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/eInvoice/${invoiceId}`,
  })
}

const getEInvoiceStatus = (axios: IFactoryFunctionRequest<any, any>) => (invoiceId: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/eInvoice/${invoiceId}/status`,
  })
}

const downloadEInvoice = (axios: IFactoryFunctionRequest<{ base64: string; fileName: string }, any>) => (invoiceId: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/eInvoice/${invoiceId}/download`,
  })
}

const downloadEInvoiceBySuperpdp = (axios: IFactoryFunctionRequest<{ base64: string; fileName: string }, any>) => (params: {
  superpdpId: string
  invoicingInformationId: string
  invoiceNumber?: string | null
  invoiceType?: 0 | 1
}) => {
  const query: Record<string, string | number> = {
    invoicingInformationId: params.invoicingInformationId,
  }
  if (params.invoiceNumber) query.invoiceNumber = params.invoiceNumber
  if (params.invoiceType === 1) query.invoiceType = 1
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/eInvoice/superpdp/${params.superpdpId}/download`,
    query,
  })
}

export type TEInvoiceEntity = {
  _id: string
  display: string
  connected: boolean
  mode?: string
}

export type TEInvoiceCollectedAmount = {
  typeCode: string
  netAmount: string | number | null
  vatRate: string | number | null
  currencyCode: string | null
}

export type TEInvoiceEvent = {
  id?: string | null
  statusCode: string | null
  statusText: string | null
  createdAt: string | null
  reasons?: string[]
  amounts?: TEInvoiceCollectedAmount[]
  summary?: string | null
}

export type TEInvoiceItem = {
  superpdpId: string
  status: string | null
  createdAt: string | null
  issueDate: string | null
  invoiceNumber: string | null
  invoiceType: 0 | 1
  invoiceNumberReference: string | null
  buyerName: string | null
  amountHT: number
  amountTTC: number
  vat: number
  currency: string
  invoicingInformationId: string
  invoicingInformationDisplay: string
  localInvoiceId: string | null
  localProjectId: string | null
  rejectionSummary?: string | null
  events: TEInvoiceEvent[]
}

export type TEInvoiceListResponse = {
  items: TEInvoiceItem[]
  hasAfter: boolean
  nextStartingAfterId: string | null
  entities: TEInvoiceEntity[]
  invoicingInformationId: string | null
  errors: { invoicingInfoId: string; message: string }[]
}

export type TEInvoiceListQuery = {
  invoicingInformationId?: string | null
  date?: string | null
  startingAfterId?: string | null
  limit?: number
  status?: string | null
}

const listEInvoices = (axios: IFactoryFunctionRequest<TEInvoiceListResponse, undefined>) => (query: TEInvoiceListQuery = {}) => {
  const cleaned: Record<string, string | number> = {}
  if (query.invoicingInformationId) cleaned.invoicingInformationId = query.invoicingInformationId
  if (query.date) cleaned.date = query.date
  if (query.startingAfterId) cleaned.startingAfterId = query.startingAfterId
  if (query.limit) cleaned.limit = query.limit
  if (query.status) cleaned.status = query.status
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/eInvoices`,
    query: cleaned,
  })
}

export default (axios: AxiosInstance) => ({
  postNewLine: postNewLine(useAxios(axios)),
  getAllInvoices: getAllInvoices(useAxios(axios)),
  updateInvoice: updateInvoice(useAxios(axios)),
  deleteInvoice: deleteInvoice(useAxios(axios)),
  updateInvoiceStatus: updateInvoiceStatus(useAxios(axios)),
  getInvoiceNumber: getInvoiceNumber(useAxios(axios)),
  getInvoiceDraft: getInvoiceDraft(useAxios(axios)),
  emitInvoice: emitInvoice(useAxios(axios)),
  addLog: addLog(useAxios(axios)),
  deleteLog: deleteLog(useAxios(axios)),
  newEmittedCreditNote: newEmittedCreditNote(useAxios(axios)),
  globalSearch: globalSearch(useAxios(axios)),
  addCollectionToInvoices: addCollectionToInvoices(useAxios(axios)),
  exportInvoicesToExcel: exportInvoicesToExcel(useAxios(axios)),
  reconcileBankStatement: reconcileBankStatement(useAxios(axios)),
  submitEInvoice: submitEInvoice(useAxios(axios)),
  getEInvoiceStatus: getEInvoiceStatus(useAxios(axios)),
  downloadEInvoice: downloadEInvoice(useAxios(axios)),
  downloadEInvoiceBySuperpdp: downloadEInvoiceBySuperpdp(useAxios(axios)),
  listEInvoices: listEInvoices(useAxios(axios)),
})
